import {
  Checkbox,
  FormControlLabel,
  IconButton,
  InfoCircleIcon,
  Stack
} from '@customink/pigment-react';
import {styled} from '@customink/pigment-react/lib/themeCustomink';
import {capitalize} from '@mui/material';
import PropTypes from 'prop-types';
import * as React from 'react';
import {COMPARE_LIMIT} from 'algolia_listing_pages/internal/ComparisonForm/ComparisonForm';
import QuickView from 'features/InternalTools/QuickView/QuickView';
import {fetchColorsById} from 'hooks/queries/colorsById';
import {fetchStyleById} from 'hooks/queries/styleById';

const InternalToolsWrapper = styled(Stack)({
  border: '1px dashed rgba(250, 60, 0, 0.8)',
  borderRadius: '0.1875rem',
  fontSize: '0.875rem',
  margin: 'auto',
  padding: '0.5rem 0.5rem 0',
  textAlign: 'center',
  width: '100%'
});

const InternalToolsContainer = styled(Stack)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'flex-end'
});

const InternalToolsQuickDetails = styled('div')({
  padding: '0.5rem'
});

const InternalToolsQuickViewButtonContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  margin: '0.5rem auto'
});

const InternalToolsCompareContainer = styled('div')({
  '& label': {
    margin: '0 auto'
  },
  alignItems: 'center',
  display: 'flex',

  justifyContent: 'center'
});

const InternalTools = ({
  colorLimit,
  compareHandler,
  compareStyles,
  decorationMethod,
  productName,
  productPath,
  speciality,
  styleId
}) => {
  const [quickViewOpen, setQuickViewOpen] = React.useState(false);

  const isBeingCompared = compareStyles.some(
    (style) => style.product_id === styleId
  );

  const handleCompareChange = async (event) => {
    event.stopPropagation();

    if (compareStyles.some((style) => style.product_id === styleId)) {
      compareHandler(
        compareStyles.filter((style) => style.product_id !== styleId),
        CustomInk.Metrics.fireEvent('compare products', 'deselect', 'card')
      );
      return;
    }

    if (compareStyles.length >= COMPARE_LIMIT) {
      return;
    }

    const activeProductImage = document.querySelector(
      `#product-image-${styleId}`
    ).src;

    const comparedProduct = {
      compareImageUrl: activeProductImage,
      loading: true,
      name: productName,
      product_id: styleId,
      productLink: productPath
    };

    compareHandler(
      [...compareStyles, comparedProduct],
      CustomInk.Metrics.fireEvent(
        'compare products',
        'select',
        `for select ${compareStyles.length + 1}`
      )
    );

    const [styleData, colorsData] = await Promise.all([
      fetchStyleById(styleId),
      fetchColorsById(styleId)
    ]);

    const newStyle = {
      ...styleData,
      colors: colorsData,
      sizes: styleData.sizes.map((size) => size.name)
    };

    compareHandler(
      [
        ...compareStyles.filter((style) => style.product_id !== styleId),
        {...comparedProduct, ...newStyle, loading: false}
      ],
      CustomInk.Metrics.fireEvent('compare products', 'select', 'card')
    );
  };

  return (
    <InternalToolsContainer>
      <InternalToolsWrapper
        data-testid="internal-tools"
        onClick={(e) => e.stopPropagation()}>
        {quickViewOpen && (
          <QuickView
            styleId={styleId}
            onClose={() => setQuickViewOpen(false)}
            productPath={productPath}
          />
        )}
        <InternalToolsQuickDetails>
          Ink Limit: {colorLimit}
        </InternalToolsQuickDetails>
        <InternalToolsQuickDetails>
          Decoration Type: {decorationMethod && capitalize(decorationMethod)}
        </InternalToolsQuickDetails>
        <InternalToolsQuickDetails>
          Specialty: {speciality.toString()}
        </InternalToolsQuickDetails>
        <InternalToolsQuickViewButtonContainer>
          <IconButton
            data-testid="quick-view-button"
            fill
            onClick={() => setQuickViewOpen(true)}
            sx={{
              border: '1px solid #0000001c',
              borderRadius: '3rem',
              fontSize: '0.875rem',
              gap: '0.5rem',
              width: 'auto'
            }}>
            <InfoCircleIcon />
            Quick View
          </IconButton>
        </InternalToolsQuickViewButtonContainer>
        <InternalToolsCompareContainer>
          <FormControlLabel
            control={<Checkbox checked={isBeingCompared} size="medium" />}
            label="Compare"
            onClick={handleCompareChange}
          />
        </InternalToolsCompareContainer>
      </InternalToolsWrapper>
    </InternalToolsContainer>
  );
};

export default InternalTools;

InternalTools.propTypes = {
  colorLimit: PropTypes.number,
  compareHandler: PropTypes.func,
  compareStyles: PropTypes.array,
  decorationMethod: PropTypes.string,
  productPath: PropTypes.string,
  speciality: PropTypes.bool,
  styleId: PropTypes.number
};
